import React, { useState, useEffect, useCallback } from 'react';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format, startOfMonth, subDays, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';

const COP = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n) || 0);
const num = (v) => parseFloat(v) || 0;

const ACCESOS = [
  { label: 'Hoy',          inicio: () => format(new Date(), 'yyyy-MM-dd'),                                    fin: () => format(new Date(), 'yyyy-MM-dd') },
  { label: 'Ayer',         inicio: () => format(subDays(new Date(), 1), 'yyyy-MM-dd'),                        fin: () => format(subDays(new Date(), 1), 'yyyy-MM-dd') },
  { label: 'Esta semana',  inicio: () => format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'), fin: () => format(new Date(), 'yyyy-MM-dd') },
  { label: 'Este mes',     inicio: () => format(startOfMonth(new Date()), 'yyyy-MM-dd'),                       fin: () => format(new Date(), 'yyyy-MM-dd') },
];

export default function VentasUnificadas() {
  const hoy = format(new Date(), 'yyyy-MM-dd');
  const [inicio, setInicio]     = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [fin, setFin]           = useState(hoy);
  const [bodegas, setBodegas]   = useState([]);
  const [bodegaId, setBodegaId] = useState('');
  const [datos, setDatos]       = useState(null);
  const [cargando, setCargando] = useState(false);
  const [vista, setVista]       = useState('dia'); // 'dia' | 'sucursal' | 'pago'

  useEffect(() => {
    API.get('/bodegas').then(r => setBodegas(r.data.filter(b => b.tipo === 'punto_venta'))).catch(() => {});
  }, []);

  const cargar = useCallback(() => {
    setCargando(true);
    API.get('/ventas/unificadas', { params: { inicio, fin, bodega_id: bodegaId || null } })
      .then(r => setDatos(r.data))
      .catch(() => toast.error('Error cargando ventas'))
      .finally(() => setCargando(false));
  }, [inicio, fin, bodegaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const t = datos?.totales;
  const dias = datos?.dias || [];

  // Agrupar por sucursal
  const porSucursal = {};
  dias.forEach(d => {
    const key = d.bodega_id || 'sin';
    if (!porSucursal[key]) porSucursal[key] = { bodega: d.bodega, total: 0, facturas: 0, efectivo: 0, tarjeta: 0, rappi: 0, bonos: 0, nequi: 0 };
    const s = porSucursal[key];
    s.total    += num(d.aliaddo?.total) + num(d.pos?.total);
    s.facturas += num(d.aliaddo?.facturas) + num(d.pos?.ventas);
    s.efectivo += num(d.aliaddo?.efectivo) + num(d.pos?.efectivo);
    s.tarjeta  += num(d.aliaddo?.tarjeta_debito) + num(d.aliaddo?.tarjeta_credito) + num(d.pos?.tarjeta);
    s.rappi    += num(d.aliaddo?.rappi);
    s.bonos    += num(d.aliaddo?.bonos);
    s.nequi    += num(d.aliaddo?.nequi) + num(d.pos?.qr);
  });

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>📊 Ventas Unificadas</h1>
        <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>Aliaddo + POS Emergencia — todos los puntos de venta</p>
      </div>

      {/* Filtros */}
      <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 18 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={st.label}>Desde</div>
            <input type="date" value={inicio} onChange={e => setInicio(e.target.value)} max={fin} style={st.input} />
          </div>
          <div>
            <div style={st.label}>Hasta</div>
            <input type="date" value={fin} onChange={e => setFin(e.target.value)} max={hoy} style={st.input} />
          </div>
          <div>
            <div style={st.label}>Punto de venta</div>
            <select value={bodegaId} onChange={e => setBodegaId(e.target.value)} style={st.input}>
              <option value="">Todos</option>
              {bodegas.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {ACCESOS.map(a => (
              <button key={a.label} onClick={() => { setInicio(a.inicio()); setFin(a.fin()); }}
                style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#6B7280' }}>
                {a.label}
              </button>
            ))}
          </div>
          {cargando && <span style={{ fontSize: 13, color: '#6B7280', alignSelf: 'center' }}>⏳ Cargando...</span>}
        </div>
      </div>

      {/* Tarjetas resumen */}
      {t && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px,1fr))', gap: 12, marginBottom: 18 }}>
          {[
            { label: 'Total ventas',    valor: COP(t.total),       icon: '💰', color: '#10B981' },
            { label: 'Transacciones',   valor: t.facturas.toLocaleString(), icon: '🧾', color: '#3B82F6' },
            { label: 'Ticket promedio', valor: t.facturas > 0 ? COP(t.total / t.facturas) : '—', icon: '📊', color: '#8B5CF6' },
            { label: 'Efectivo',        valor: COP(t.efectivo),    icon: '💵', color: '#059669' },
            { label: 'Tarjeta',         valor: COP(t.tarjeta),     icon: '💳', color: '#2563EB' },
            { label: 'Nequi / QR',      valor: COP(t.nequi + t.transferencias + t.daviplata), icon: '📱', color: '#0EA5E9' },
            { label: 'Rappi',           valor: COP(t.rappi),       icon: '🛵', color: '#F97316' },
            { label: 'Bonos',           valor: COP(t.bonos),       icon: '🎟️', color: '#7C3AED' },
          ].map((s, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ fontSize: 18, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 15, color: s.color }}>{s.valor}</div>
              <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'white', borderRadius: 10, padding: 3, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', width: 'fit-content' }}>
        {[{ id: 'dia', label: '📅 Por día' }, { id: 'sucursal', label: '🏪 Por sucursal' }, { id: 'pago', label: '💳 Por medio de pago' }].map(v => (
          <button key={v.id} onClick={() => setVista(v.id)} style={{
            padding: '7px 16px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13,
            fontWeight: vista === v.id ? 700 : 500,
            background: vista === v.id ? '#1a1a2e' : 'transparent',
            color: vista === v.id ? 'white' : '#6B7280'
          }}>{v.label}</button>
        ))}
      </div>

      {dias.length === 0 && !cargando && (
        <div style={{ background: 'white', borderRadius: 14, padding: 40, textAlign: 'center', color: '#9CA3AF', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          📭 Sin ventas en este período
        </div>
      )}

      {/* Vista por día */}
      {vista === 'dia' && dias.length > 0 && (
        <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#1a1a2e' }}>
                  {['Día','PDV','Aliaddo','POS Emerg.','Total','Efectivo','Tarjeta','Nequi/QR','Rappi','Bonos'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Día' || h === 'PDV' ? 'left' : 'right', color: 'white', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dias.map((d, i) => {
                  const totalDia = num(d.aliaddo?.total) + num(d.pos?.total);
                  const efectivo = num(d.aliaddo?.efectivo) + num(d.pos?.efectivo);
                  const tarjeta  = num(d.aliaddo?.tarjeta_debito) + num(d.aliaddo?.tarjeta_credito) + num(d.pos?.tarjeta);
                  const nequi    = num(d.aliaddo?.nequi) + num(d.aliaddo?.transferencias) + num(d.aliaddo?.daviplata) + num(d.pos?.qr);
                  return (
                    <tr key={i} style={{ borderTop: '1px solid #F3F4F6', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {format(new Date(d.dia + 'T12:00'), "d 'de' MMM", { locale: es })}
                        <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}>{format(new Date(d.dia + 'T12:00'), 'EEEE', { locale: es })}</div>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#6B7280', fontSize: 12 }}>{d.bodega || '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', color: '#10B981' }}>
                        {d.aliaddo ? COP(d.aliaddo.total) : '—'}
                        {d.aliaddo && <div style={{ fontSize: 10, color: '#9CA3AF' }}>{d.aliaddo.facturas} fact.</div>}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', color: '#F59E0B' }}>
                        {d.pos ? COP(d.pos.total) : '—'}
                        {d.pos && <div style={{ fontSize: 10, color: '#9CA3AF' }}>{d.pos.ventas} vent.</div>}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, fontSize: 14 }}>{COP(totalDia)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', color: '#059669' }}>{efectivo > 0 ? COP(efectivo) : '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', color: '#2563EB' }}>{tarjeta > 0 ? COP(tarjeta) : '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', color: '#0EA5E9' }}>{nequi > 0 ? COP(nequi) : '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', color: '#F97316' }}>{num(d.aliaddo?.rappi) > 0 ? COP(d.aliaddo.rappi) : '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', color: '#7C3AED' }}>{num(d.aliaddo?.bonos) > 0 ? COP(d.aliaddo.bonos) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
              {t && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #E5E7EB', background: '#F9FAFB' }}>
                    <td colSpan={2} style={{ padding: '10px 12px', fontWeight: 800, fontSize: 14 }}>Total período</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#10B981' }}>{COP(dias.reduce((s,d) => s + num(d.aliaddo?.total), 0))}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#F59E0B' }}>{COP(dias.reduce((s,d) => s + num(d.pos?.total), 0))}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, fontSize: 15 }}>{COP(t.total)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#059669' }}>{COP(t.efectivo)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#2563EB' }}>{COP(t.tarjeta)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#0EA5E9' }}>{COP(t.nequi + t.transferencias + t.daviplata)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#F97316' }}>{COP(t.rappi)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#7C3AED' }}>{COP(t.bonos)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Vista por sucursal */}
      {vista === 'sucursal' && dias.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Object.values(porSucursal).sort((a,b) => b.total - a.total).map((s, i) => {
            const pctEfectivo = s.total > 0 ? s.efectivo / s.total * 100 : 0;
            const pctTarjeta  = s.total > 0 ? s.tarjeta  / s.total * 100 : 0;
            const pctNequi    = s.total > 0 ? s.nequi    / s.total * 100 : 0;
            const pctRappi    = s.total > 0 ? s.rappi    / s.total * 100 : 0;
            return (
              <div key={i} style={{ background: 'white', borderRadius: 14, padding: '18px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>🏪 {s.bodega || 'Sin PDV'}</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{s.facturas.toLocaleString()} transacciones</div>
                  </div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 22, color: '#10B981' }}>{COP(s.total)}</div>
                </div>
                <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', background: '#F3F4F6', display: 'flex', marginBottom: 10 }}>
                  <div style={{ width: `${pctEfectivo}%`, background: '#059669' }} />
                  <div style={{ width: `${pctTarjeta}%`,  background: '#2563EB' }} />
                  <div style={{ width: `${pctNequi}%`,    background: '#0EA5E9' }} />
                  <div style={{ width: `${pctRappi}%`,    background: '#F97316' }} />
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
                  {[
                    { label: '💵 Efectivo', val: s.efectivo, color: '#059669' },
                    { label: '💳 Tarjeta',  val: s.tarjeta,  color: '#2563EB' },
                    { label: '📱 Nequi',    val: s.nequi,    color: '#0EA5E9' },
                    { label: '🛵 Rappi',    val: s.rappi,    color: '#F97316' },
                    { label: '🎟️ Bonos',   val: s.bonos,    color: '#7C3AED' },
                  ].filter(x => x.val > 0).map((x, j) => (
                    <span key={j} style={{ color: x.color, fontWeight: 700 }}>{x.label}: {COP(x.val)}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Vista por medio de pago */}
      {vista === 'pago' && t && (
        <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ background: '#1a1a2e', padding: '12px 20px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'white', fontWeight: 800, fontSize: 14 }}>💳 Desglose por medio de pago</span>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace', fontWeight: 700 }}>{COP(t.total)}</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                {['Medio de pago', 'Total', '% del total', 'Destino'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: h === 'Medio de pago' || h === 'Destino' ? 'left' : 'right', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { label: '💵 Efectivo',        val: t.efectivo,                               color: '#059669', destino: 'Caja — va al banco',      importante: true },
                { label: '💳 Tarjeta',          val: t.tarjeta,                                color: '#2563EB', destino: 'Datafono → cuenta' },
                { label: '📱 Nequi',            val: t.nequi,                                  color: '#0EA5E9', destino: 'Cuenta bancaria' },
                { label: '🏦 Transferencias',   val: t.transferencias,                         color: '#0284C7', destino: 'Cuenta bancaria' },
                { label: '📲 Daviplata / Dale', val: t.daviplata,                              color: '#7C3AED', destino: 'Cuenta bancaria' },
                { label: '🛵 Rappi',            val: t.rappi,                                  color: '#F97316', destino: 'Liquidación Rappi' },
                { label: '🎟️ Bonos Coomeva',   val: t.bonos,                                  color: '#8B5CF6', destino: 'Liquidación Coomeva' },
                { label: '🔀 Pago mixto',       val: t.mixto,                                  color: '#9CA3AF', destino: 'Verificar manualmente' },
              ].filter(r => r.val > 0).map((r, i) => (
                <tr key={i} style={{ borderTop: '1px solid #F3F4F6', background: r.importante ? '#F0FDF4' : i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                  <td style={{ padding: '11px 16px', fontWeight: 700 }}>{r.label}</td>
                  <td style={{ padding: '11px 16px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: r.color, fontSize: 14 }}>{COP(r.val)}</td>
                  <td style={{ padding: '11px 16px', textAlign: 'right', color: '#6B7280' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                      <div style={{ width: 60, height: 6, background: '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.round(r.val / t.total * 100)}%`, height: '100%', background: r.color, borderRadius: 3 }} />
                      </div>
                      {Math.round(r.val / t.total * 100)}%
                    </div>
                  </td>
                  <td style={{ padding: '11px 16px', fontSize: 12, color: '#6B7280' }}>{r.destino}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid #E5E7EB', background: '#F9FAFB' }}>
                <td style={{ padding: '12px 16px', fontWeight: 800, fontSize: 14 }}>Total</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, fontSize: 15, color: '#1a1a2e' }}>{COP(t.total)}</td>
                <td style={{ padding: '12px 16px', textAlign: 'right', color: '#6B7280' }}>100%</td>
                <td style={{ padding: '12px 16px', fontSize: 12, color: '#9CA3AF' }}>{t.facturas.toLocaleString()} transacciones</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const st = {
  label: { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 },
  input: { padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white' },
};
